<template>
  <div class="language-switcher">
    <svg-icon icon="language" class="icon--language" />
    <label class="language-switcher__label">{{ $t('header.language') }}:</label>
    <span class="language-switcher__active-language">
      {{ currentLocale.shortLabel }}
    </span>
    <svg-icon icon="arrow_down_small" class="icon--arrow-down" />
    <ul class="language-switcher__other-languages">
      <div class="language-switcher__other-languages-filler"></div>
      <li
        v-for="locale in locales"
        :key="locale.id"
        class="language-switcher__other-language"
        @click="switchLocale(locale.id)"
      >
        {{ locale.label }}
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import useLocaleSwitcher from '@/composables/useLocaleSwitcher';
import { Locales } from '@/services/locales';
import { defineComponent } from 'vue';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'LocaleSwitcher',

  setup() {
    const { locales, currentLocale, switchLocale } = useLocaleSwitcher();
    const route = useRoute();
    if (route.params.lang) {
      const locale = Locales.getLocaleByShortLabel(route.params.lang as string);
      if (locale) {
        switchLocale(locale.id);
      }
    }

    return {
      locales,
      currentLocale,
      switchLocale,
    };
  },
});
</script>

<style lang="scss" scoped>
@use 'sass:map';

.language-switcher {
  position: relative;
  text-align: right;
  z-index: map.get($z-index, 'header');
  display: flex;
  flex-flow: row nowrap;

  &__active-language {
    margin-left: 5px;
    color: var(--secondary-color);
  }

  &__other-languages {
    display: none;
    position: absolute;
    right: -23px;
    top: 35px;
    background-color: $white;
    margin: 0;
    padding: 10px 0;
    border: 1px solid rgba($black, 0.08);
    border-radius: 2px;
    box-shadow: -1px 4px 6px rgba($black, 0.08);
    text-align: left;

    &::before,
    &::after {
      bottom: 100%;
      left: 75%;
      border: solid transparent;
      content: '';
      height: 0;
      width: 0;
      position: absolute;
      pointer-events: none;
    }

    &::after {
      border-bottom-color: $white;
      border-width: 8px;
      margin-left: -8px;
    }

    &::before {
      border-bottom-color: rgba($black, 0.08);
      border-width: 9px;
      margin-left: -9px;
    }

    &-filler {
      position: absolute;
      top: -15px;
      width: 100%;
      background-color: transparent;
      height: 15px;
    }
  }

  &__other-language {
    list-style-type: none;
    padding: 10px 25px;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: var(--secondary-color);
    }
  }

  &:hover {
    .icon--arrow-down {
      transform: rotate(180deg);
    }

    .language-switcher {
      &__other-languages {
        display: block;
      }
    }
  }
}

.icon {
  &--language {
    margin-right: 5px;
    @include svgColor(var(--secondary-color));
  }

  &--arrow-down {
    transition: transform 0.2s;
  }
}
</style>
